import React, { useState, useEffect } from 'react';
import { ProfilePhoto, PhotoValidationResult } from '../types';
import {
  PhotoValidatorContainer,
  PhotoValidatorHeader,
  PhotoValidatorTitle,
  PhotoValidatorDescription,
  PhotoGrid,
  PhotoItem,
  PhotoPreview,
  PhotoStatus,
  StatusBadge,
  ValidationDetails,
  ValidationIssue,
  IssueType,
  IssueMessage,
  IssueSeverity,
  ValidationSummary,
  SummaryItem,
  SummaryValue,
  SummaryLabel,
  ValidationActions,
  ActionButton,
  RetryButton,
  ApproveButton,
  RejectButton,
  ValidationProgress,
  ProgressBar,
  ProgressText
} from '../../../styles/Profile/ProfilePreview.styles';

interface PhotoValidatorProps {
  photos: ProfilePhoto[];
  onValidationChange: (results: PhotoValidationResult) => void;
}

const VALIDATION_RULES = [
  {
    id: 'quality',
    name: 'Photo Quality',
    description: 'Photos should be clear and well-lit',
    weight: 30
  },
  {
    id: 'content',
    name: 'Content Guidelines',
    description: 'Photos must follow our content guidelines',
    weight: 40
  },
  {
    id: 'size',
    name: 'File Size',
    description: 'Photos should be under 10MB',
    weight: 15
  },
  {
    id: 'format',
    name: 'File Format',
    description: 'Photos should be JPEG, PNG, or WebP',
    weight: 15
  }
];

export const PhotoValidator: React.FC<PhotoValidatorProps> = ({
  photos,
  onValidationChange
}) => {
  const [validationResults, setValidationResults] = useState<PhotoValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);

  const validatePhoto = async (photo: ProfilePhoto): Promise<PhotoValidationResult> => {
    // Simulate photo validation process
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const issues: PhotoValidationResult['issues'] = [];
    
    // Simulate validation checks
    if (Math.random() > 0.7) {
      issues.push({
        message: 'Photo quality could be improved',
        severity: 'warning' as const
      });
    }
    
    if (Math.random() > 0.8) {
      issues.push({
        message: 'Photo may not meet content guidelines',
        severity: 'error' as const
      });
    }
    
    if (Math.random() > 0.9) {
      issues.push({
        message: 'Photo file size is large',
        severity: 'warning' as const
      });
    }
    
    return {
      photoId: photo.id,
      isValid: issues.filter((issue: { severity: string; message: string }) => issue.severity === 'error').length === 0,
      errors: issues.filter((issue: { severity: string; message: string }) => issue.severity === 'error').map((issue: { severity: string; message: string }) => issue.message),
      warnings: issues.filter((issue: { severity: string; message: string }) => issue.severity === 'warning').map((issue: { severity: string; message: string }) => issue.message),
      issues
    };
  };

  const validateAllPhotos = async () => {
    setIsValidating(true);
    setValidationProgress(0);
    
    const results: PhotoValidationResult[] = [];
    
    for (let i = 0; i < photos.length; i++) {
      const result = await validatePhoto(photos[i]);
      results.push(result);
      setValidationProgress(((i + 1) / photos.length) * 100);
    }
    
    setValidationResults(results);
    setIsValidating(false);
    
    // Calculate overall validation result
    const overallResult: PhotoValidationResult = {
      photoId: 'overall',
      isValid: results.every(result => result.isValid),
      errors: results.flatMap(result => result.errors),
      warnings: results.flatMap(result => result.warnings),
      issues: results.flatMap(result => result.issues)
    };
    
    onValidationChange(overallResult);
  };

  const retryValidation = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;
    
    const result = await validatePhoto(photo);
    setValidationResults(prev => 
      prev.map(r => r.photoId === photoId ? result : r)
    );
    
    // Recalculate overall result
    const updatedResults = validationResults.map(r => r.photoId === photoId ? result : r);
    const overallResult: PhotoValidationResult = {
      photoId: 'overall',
      isValid: updatedResults.every(result => result.isValid),
      errors: updatedResults.flatMap(result => result.errors),
      warnings: updatedResults.flatMap(result => result.warnings),
      issues: updatedResults.flatMap(result => result.issues)
    };
    
    onValidationChange(overallResult);
  };

  const approvePhoto = (photoId: string) => {
    setValidationResults(prev => 
      prev.map(r => r.photoId === photoId ? { ...r, isValid: true, errors: [] } : r)
    );
    
    // Recalculate overall result
    const updatedResults = validationResults.map(r => 
      r.photoId === photoId ? { ...r, isValid: true, errors: [] } : r
    );
    const overallResult: PhotoValidationResult = {
      photoId: 'overall',
      isValid: updatedResults.every(result => result.isValid),
      errors: updatedResults.flatMap(result => result.errors),
      warnings: updatedResults.flatMap(result => result.warnings),
      issues: updatedResults.flatMap(result => result.issues)
    };
    
    onValidationChange(overallResult);
  };

  const rejectPhoto = (photoId: string) => {
    setValidationResults(prev => 
      prev.map(r => r.photoId === photoId ? { ...r, isValid: false, errors: ['Photo rejected by user'] } : r)
    );
    
    // Recalculate overall result
    const updatedResults = validationResults.map(r => 
      r.photoId === photoId ? { ...r, isValid: false, errors: ['Photo rejected by user'] } : r
    );
    const overallResult: PhotoValidationResult = {
      photoId: 'overall',
      isValid: updatedResults.every(result => result.isValid),
      errors: updatedResults.flatMap(result => result.errors),
      warnings: updatedResults.flatMap(result => result.warnings),
      issues: updatedResults.flatMap(result => result.issues)
    };
    
    onValidationChange(overallResult);
  };

  useEffect(() => {
    if (photos.length > 0 && validationResults.length === 0) {
      validateAllPhotos();
    }
  }, [photos]);

  const getValidationSummary = () => {
    const totalPhotos = photos.length;
    const validPhotos = validationResults.filter(r => r.isValid).length;
    const invalidPhotos = totalPhotos - validPhotos;
    const totalErrors = validationResults.reduce((sum, r) => sum + r.errors.length, 0);
    const totalWarnings = validationResults.reduce((sum, r) => sum + r.warnings.length, 0);
    
    return {
      totalPhotos,
      validPhotos,
      invalidPhotos,
      totalErrors,
      totalWarnings,
      validationRate: totalPhotos > 0 ? (validPhotos / totalPhotos) * 100 : 0
    };
  };

  const summary = getValidationSummary();

  const renderPhotoItem = (photo: ProfilePhoto) => {
    const result = validationResults.find(r => r.photoId === photo.id);
    const isValid = result?.isValid ?? false;
    const hasErrors = result?.errors.length ?? 0;
    const hasWarnings = result?.warnings.length ?? 0;
    
    return (
      <PhotoItem key={photo.id} $isValid={isValid}>
        <PhotoPreview>
          <img src={photo.url} alt={`Photo ${photo.order + 1}`} />
          <PhotoStatus>
            <StatusBadge $status={isValid ? 'valid' : 'invalid'}>
              {isValid ? '✓ Valid' : '✗ Invalid'}
            </StatusBadge>
            {photo.isMain && (
              <StatusBadge $status="main">Main Photo</StatusBadge>
            )}
          </PhotoStatus>
        </PhotoPreview>
        
        <ValidationDetails>
          {result?.issues.map((issue, index) => (
            <ValidationIssue key={index}>
              <IssueType $severity={issue.severity}>
                {issue.severity === 'error' ? 'Error' : 'Warning'}
              </IssueType>
              <IssueMessage>{issue.message}</IssueMessage>
            </ValidationIssue>
          ))}
          
          {result && (
            <ValidationActions>
              <RetryButton onClick={() => retryValidation(photo.id)}>
                Retry
              </RetryButton>
              <ApproveButton onClick={() => approvePhoto(photo.id)}>
                Approve
              </ApproveButton>
              <RejectButton onClick={() => rejectPhoto(photo.id)}>
                Reject
              </RejectButton>
            </ValidationActions>
          )}
        </ValidationDetails>
      </PhotoItem>
    );
  };

  return (
    <PhotoValidatorContainer>
      <PhotoValidatorHeader>
        <PhotoValidatorTitle>Photo Validation</PhotoValidatorTitle>
        <PhotoValidatorDescription>
          We automatically validate your photos to ensure they meet our quality and content guidelines.
        </PhotoValidatorDescription>
      </PhotoValidatorHeader>

      {isValidating && (
        <ValidationProgress>
          <ProgressBar>
            <div 
              style={{ 
                width: `${validationProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }}
            />
          </ProgressBar>
          <ProgressText>
            Validating photos... {Math.round(validationProgress)}%
          </ProgressText>
        </ValidationProgress>
      )}

      <ValidationSummary>
        <SummaryItem>
          <SummaryValue>{summary.totalPhotos}</SummaryValue>
          <SummaryLabel>Total Photos</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.validPhotos}</SummaryValue>
          <SummaryLabel>Valid</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.invalidPhotos}</SummaryValue>
          <SummaryLabel>Invalid</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.totalErrors}</SummaryValue>
          <SummaryLabel>Errors</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{summary.totalWarnings}</SummaryValue>
          <SummaryLabel>Warnings</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>{Math.round(summary.validationRate)}%</SummaryValue>
          <SummaryLabel>Validation Rate</SummaryLabel>
        </SummaryItem>
      </ValidationSummary>

      <PhotoGrid>
        {photos.map(photo => renderPhotoItem(photo))}
      </PhotoGrid>

      <div style={{ marginTop: '1rem' }}>
        <h4>Validation Rules:</h4>
        {VALIDATION_RULES.map(rule => (
          <div key={rule.id} style={{ marginBottom: '0.5rem' }}>
            <strong>{rule.name}</strong> ({rule.weight}%): {rule.description}
          </div>
        ))}
      </div>
    </PhotoValidatorContainer>
  );
};
